/**
 * VerifyShelf — the project navigation's VERIFY group, as shelf cards.
 *
 * Phase 6 §"6-shelf reserve". Row c0c4dc39: this shelf used to hand-list
 * Tests, Defects and "Quality" while the navigation listed Tests, QA Agent,
 * Conflicts and Traceability under VERIFY. It now renders getVerifyItems() —
 * the same function the navigation's VERIFY section is built from — so the two
 * cannot disagree. Defects stays under EXECUTE in the navigation, as before.
 */

import { getVerifyItems } from "../navData";
import { ShelfCard } from "./ShelfCard";
import type { ShelfLabelResolver } from "./types";
import { IDENTITY_SHELF_LABEL } from "./types";

interface Props {
  projectId?: string | null;
  /** Template-layer vocabulary SKIN resolver (supplied by ShelfSidebar; one
   *  deduped fetch). Passed straight to getVerifyItems, so entity-type cards
   *  skin exactly as the navigation rows do. Defaults to canonical. */
  label?: ShelfLabelResolver;
}

/** ``/projects/p/qa-agent`` -> ``verify-qa-agent``. */
function verifyCardTestId(path: string): string {
  return `verify-${path.split("/").filter(Boolean).pop() ?? ""}`;
}

export function VerifyShelf({ projectId, label = IDENTITY_SHELF_LABEL }: Props) {
  // Outside a project the navigation has no VERIFY group at all (every page in
  // it is project-scoped), so the shelf names the pages but links none of them
  // rather than sending anyone to a project route with no project.
  const base = projectId ? `/projects/${projectId}` : "";
  return (
    <div
      data-testid="verify-shelf"
      role="region"
      aria-label="Verify"
      className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2"
    >
      {getVerifyItems(base, label).map((item) => (
        <ShelfCard
          key={item.path}
          to={item.path}
          label={item.label}
          testId={verifyCardTestId(item.path)}
          unavailable={!projectId}
        />
      ))}
    </div>
  );
}
